import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { orderAPI } from '../../services/api';

interface Order {
  id: string;
  orderno: string;
  totalamount: number;
  date: string;
  totalitems: number;
  city: {
    id: string;
    name: string;
    status: number;
  };
  orderstatus: number;
  user?: {
    id: string;
    username: string;
    email: string;
  };
  orderdetails?: Array<{
    id: string;
    quantity: number;
    amount: number;
    productId: string;
  }>;
}

interface OrdersState {
  orders: Order[];
  selectedOrder: Order | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    items_per_page: number;
    total: number;
  };
}

const initialState: OrdersState = {
  orders: [],
  selectedOrder: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    items_per_page: 10,
    total: 0,
  },
};

// Fetch orders with pagination and filters
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params?: { 
    page?: number; 
    items_per_page?: number; 
    search?: string; 
    'where[orderstatus]'?: string;
    sort?: string;
  }) => {
    const response = await orderAPI.getOrdersV2(params);
    
    // Handle new API response structure - same as products
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return {
        orders: response.data.data,
        pagination: response.data.payload || {
          page: 1,
          items_per_page: 10,
          total: 0,
        }
      };
    } else if (Array.isArray(response.data)) {
      return {
        orders: response.data,
        pagination: {
          page: 1,
          items_per_page: response.data.length,
          total: response.data.length,
        }
      };
    } else {
      return {
        orders: [],
        pagination: {
          page: 1,
          items_per_page: 10,
          total: 0,
        }
      };
    }
  }
);

// Fetch order by ID
export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId: string) => {
    const response = await orderAPI.getOrder(orderId);
    return response.data;
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch orders';
      })
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state) => {
        state.loading = false;
        state.selectedOrder = null;
      });
  },
});

export default ordersSlice.reducer;